/**
 * Compute the fiscal year a campus started in, based on its start date.
 * FY boundary: April 1. If month >= 4, startFY = year + 1; else startFY = year.
 */
function campusStartFY(startDate: string): number {
  const parts = startDate.split("-");
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  return m >= 4 ? y + 1 : y;
}

/**
 * Compute which campus year we're in for a given fiscal year.
 * Returns null if startDate is null/undefined.
 * Clamps to minimum 1.
 */
export function campusYearInFY(
  startDate: string | null | undefined,
  fiscalYear: number,
): number | null {
  if (!startDate) return null;
  const fy = campusStartFY(startDate);
  const year = fiscalYear - fy + 1;
  return year > 0 ? year : 1;
}

/**
 * Format a YYYY-MM-DD start date as "Mon YYYY" (e.g., "Sep 2024").
 * Parses manually to avoid UTC date shift.
 */
export function formatStartDate(dateStr: string): string {
  const parts = dateStr.split("-");
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

/**
 * Format a fiscal year number as "FY25/26" (Apr start year / Mar end year).
 * e.g., fiscalYear 2026 → "FY25/26"
 */
export function formatFY(fiscalYear: number): string {
  const startYY = String(fiscalYear - 1).slice(-2);
  const endYY = String(fiscalYear).slice(-2);
  return `FY${startYY}/${endYY}`;
}

/**
 * Determine the current fiscal year based on today's date.
 * FY boundary: April 1. If current month >= 4, FY = year + 1; else FY = year.
 */
export function currentFiscalYear(): number {
  const now = new Date();
  const m = now.getMonth() + 1; // 1-based
  const y = now.getFullYear();
  return m >= 4 ? y + 1 : y;
}

/**
 * Returns the 12 months of a fiscal year as YYYY-MM strings in order (Apr–Mar).
 * FY2026 → ["2025-04", ..., "2025-12", "2026-01", "2026-02", "2026-03"]
 */
export function fiscalYearMonths(fiscalYear: number): string[] {
  const startYear = fiscalYear - 1;
  const months: string[] = [];
  for (let m = 4; m <= 12; m++) months.push(`${startYear}-${String(m).padStart(2, "0")}`);
  for (let m = 1; m <= 3; m++) months.push(`${fiscalYear}-${String(m).padStart(2, "0")}`);
  return months;
}

/**
 * Count Mon–Fri weekdays between two YYYY-MM-DD dates (inclusive).
 * No holiday calendar — capacity v1 treats every weekday as a working day.
 * Parses manually to avoid UTC date shift.
 */
export function weekdaysInRange(start: string, end: string): number {
  const [sy, sm, sd] = start.split("-").map(Number) as [number, number, number];
  const [ey, em, ed] = end.split("-").map(Number) as [number, number, number];
  const cur = new Date(sy, sm - 1, sd);
  const last = new Date(ey, em - 1, ed);
  let count = 0;
  while (cur <= last) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

/**
 * Build the four fiscal quarter date ranges for a given fiscal year.
 * FY2026 → Q1: 2025-04-01..2025-06-30, Q2: 2025-07-01..2025-09-30, etc.
 */
export function fiscalQuarterRanges(fiscalYear: number) {
  const fyStart = fiscalYear - 1;
  return [
    { label: "Q1", start: `${fyStart}-04-01`, end: `${fyStart}-06-30` },
    { label: "Q2", start: `${fyStart}-07-01`, end: `${fyStart}-09-30` },
    { label: "Q3", start: `${fyStart}-10-01`, end: `${fyStart}-12-31` },
    { label: "Q4", start: `${fyStart + 1}-01-01`, end: `${fyStart + 1}-03-31` },
  ] as const;
}
